import { useState, useCallback, useRef } from 'react';
import { ClipboardList } from 'lucide-react';
import { useAppStore } from '@/store';
import allergensData from '@/data/allergens.json';
import type { AllergenEntry } from '@allergen-lens/shared-logic';
import { IngredientTokenList } from './IngredientTokenList';
import { LanguageSelector } from './LanguageSelector';
import { examples, exampleDescriptions } from '@/utils/examples';
import styles from './InputPanel.module.css';

const allIngredientNames = (allergensData as AllergenEntry[]).flatMap((e) => [
  e.canonicalName,
  ...e.synonyms,
]);

export function InputPanel() {
  const rawInput = useAppStore((s) => s.rawInput);
  const setRawInput = useAppStore((s) => s.setRawInput);
  const addManualIngredient = useAppStore((s) => s.addManualIngredient);
  const tokens = useAppStore((s) => s.tokens);

  const [inputText, setInputText] = useState(rawInput);
  const [manualText, setManualText] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setRawInput(value);
    }, 300);
  }, [setRawInput]);

  const handleManualChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setManualText(value);
    if (value.length >= 2) {
      const lower = value.toLowerCase();
      const matches = allIngredientNames
        .filter((n) => n.toLowerCase().includes(lower))
        .slice(0, 8);
      setSuggestions(matches);
    } else {
      setSuggestions([]);
    }
  }, []);

  const handleAddManual = useCallback(() => {
    if (manualText.trim()) {
      addManualIngredient(manualText.trim());
      setManualText('');
      setSuggestions([]);
    }
  }, [manualText, addManualIngredient]);

  const handleSelectSuggestion = useCallback((suggestion: string) => {
    addManualIngredient(suggestion);
    setManualText('');
    setSuggestions([]);
  }, [addManualIngredient]);

  const handleClear = useCallback(() => {
    setInputText('');
    setRawInput('');
    setManualText('');
    setSuggestions([]);
  }, [setRawInput]);

  const handleExample = useCallback((value: string) => {
    setInputText(value);
    setRawInput(value);
  }, [setRawInput]);

  return (
    <div className={styles.inputPanel}>
      <div className={styles.header}>
        <label htmlFor="ingredient-input" className={styles.heading}>Ingredient List</label>
        {(rawInput || tokens.length > 0) && (
          <button className={styles.clearButton} onClick={handleClear}>Clear</button>
        )}
      </div>
      <textarea
        id="ingredient-input"
        className={styles.textarea}
        value={inputText}
        onChange={handleInputChange}
        placeholder="Paste or type the ingredient list from a product label…"
        aria-label="Ingredient list"
      />

      {tokens.length > 0 ? (
        <>
          <IngredientTokenList />
          <div className={styles.divider}>or add individually</div>
        </>
      ) : (
        <div className={styles.emptyState}>
          <ClipboardList size={28} className={styles.emptyIcon} aria-hidden />
          <p className={styles.emptyLead}>Check any ingredient list for allergens</p>
          <p>Paste a label above, or start instantly with an example below.</p>
          <div className={styles.exampleCtas}>
            <span className={styles.exampleLabel}>Try an example:</span>
            {Object.entries(examples).map(([key, value]) => (
              <button
                key={key}
                className={styles.exampleButton}
                onClick={() => handleExample(value)}
                aria-label={exampleDescriptions[key]}
              >
                {exampleDescriptions[key]}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={styles.inputRow}>
        <input
          className={styles.manualInput}
          type="text"
          value={manualText}
          onChange={handleManualChange}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddManual(); }}
          placeholder="Add a single ingredient…"
          aria-label="Add ingredient individually"
        />
        <button className={styles.addButton} onClick={handleAddManual}>Add</button>
      </div>

      {suggestions.length > 0 && (
        <div className={styles.suggestions}>
          {suggestions.map((s, i) => (
            <button key={i} className={styles.suggestionItem} onClick={() => handleSelectSuggestion(s)}>
              {s}
            </button>
          ))}
        </div>
      )}

      <LanguageSelector />
    </div>
  );
}
